import type { AnalyticsColumn, AnalyticsResult, ChartSpec } from '../../services/analyticsService';

/** Display text of one result cell. */
export function formatCell(value: unknown, column: AnalyticsColumn, language: string): string {
  if (value === null || value === undefined) return '—';
  if (column.kind === 'number' && typeof value === 'number') {
    return new Intl.NumberFormat(language, { maximumFractionDigits: 3 }).format(value);
  }
  if (column.kind === 'date' && typeof value === 'string') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    return new Intl.DateTimeFormat(language, dateOnly ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }
  if (column.kind === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** The chart settings, completed for the result: x defaults to the first non-numeric column, y to numeric ones. */
export function resolveChart(chart: ChartSpec, result: AnalyticsResult): { type: ChartSpec['type']; x: string | null; y: string[] } {
  const names = new Set(result.columns.map((c) => c.name));
  const numeric = result.columns.filter((c) => c.kind === 'number').map((c) => c.name);
  const x = chart.x && names.has(chart.x) ? chart.x : (result.columns.find((c) => c.kind !== 'number')?.name ?? result.columns[0]?.name ?? null);
  const wanted = (chart.y ?? []).filter((n) => numeric.includes(n) && n !== x);
  const y = wanted.length > 0 ? wanted : numeric.filter((n) => n !== x).slice(0, chart.type === 'pie' ? 1 : 5);
  return { type: chart.type, x, y };
}

/** The result as CSV (RFC 4180 quoting). */
export function toCsv(result: AnalyticsResult): string {
  const quote = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [result.columns.map((c) => quote(c.name)).join(',')];
  for (const row of result.rows) lines.push(row.map(quote).join(','));
  return `${lines.join('\r\n')}\r\n`;
}
